import { useEffect, useState } from "react";
import { formatMmSs, msToNextTick, remainingSeconds } from "../utils/helper/lotClock";

/**
 * A lot's countdown, re-rendered once per displayed second (#191).
 *
 * The value is computed from Date.now() during render; the timer only asks for the render. So a new
 * deadline (a bid restarted the clock) shows on the very render that brings it, never one stale
 * frame later, and a tab that was in the background shows the right time as soon as it is drawn.
 *
 * The timers are a setTimeout chain aligned to the countdown's own second boundaries (msToNextTick),
 * not a one-second interval: an interval drifts against the deadline and could skip or repeat a
 * second. The chain stops at 00:00 - what happens then is the server's call, never this hook's. A
 * browser slows timers down in a hidden tab, so the value is recomputed when the tab shows again.
 *
 * Depends only on (endsAt, frozenSeconds): new rows, figures or renders of the caller never restart
 * it.
 *
 * @param {number|null} endsAt The deadline, client epoch ms (a Live lot's clock entry.endsAt).
 * @param {number|null} frozenSeconds A Paused lot's time; when set, the countdown does not run.
 * @returns {{seconds: number, text: string}} Whole seconds left, rounded up like the server's
 *   remainingBidTime, and the same as "mm:ss". 0 / "00:00" without either argument.
 */
export const useLotCountdown = (endsAt, frozenSeconds) => {
  const [, setTick] = useState(0);
  const frozen = Number.isFinite(frozenSeconds);
  const running = !frozen && Number.isFinite(endsAt);

  useEffect(() => {
    if (!running) return undefined;

    let timeoutId = null;
    const schedule = () => {
      clearTimeout(timeoutId);
      const delay = msToNextTick(endsAt, Date.now());
      if (delay === null) return;
      timeoutId = setTimeout(() => {
        setTick((tick) => tick + 1);
        schedule();
      }, delay);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      setTick((tick) => tick + 1);
      schedule();
    };

    schedule();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [running, endsAt]);

  const seconds = frozen
    ? Math.max(0, Math.floor(frozenSeconds))
    : running
      ? remainingSeconds(endsAt, Date.now())
      : 0;

  return { seconds, text: formatMmSs(seconds) };
};
